import { useEffect, useState } from 'react';
import { EntityModal } from '../common/EntityModal';
import { SearchableSelect } from '../common/SearchableSelect';

interface SaveAsPlanModalProps {
  open: boolean;
  onClose: () => void;
  initialName: string;
  initialEventId: string;
  isSaving: boolean;
  onSave: (name: string, eventId: string) => void;
}

/** Save the canvas as a new floor plan, optionally for another event. */
export function SaveAsPlanModal({
  open,
  onClose,
  initialName,
  initialEventId,
  isSaving,
  onSave,
}: SaveAsPlanModalProps) {
  const [name, setName] = useState(initialName);
  const [eventId, setEventId] = useState(initialEventId);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setEventId(initialEventId);
    }
  }, [open, initialName, initialEventId]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Save As New Floor Plan"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(name.trim(), eventId)}
            disabled={!name.trim() || isSaving}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            data-testid="save-as-confirm-btn"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="save-as-modal">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="save-as-name">
            Name
          </label>
          <input
            id="save-as-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="save-as-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event (optional)</label>
          <SearchableSelect
            entityType="event"
            value={eventId}
            onChange={(v) => setEventId(typeof v === 'string' ? v : '')}
            placeholder="Search event..."
          />
        </div>
      </div>
    </EntityModal>
  );
}
